import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cannesEventPriceFaq, cannesEventSeo } from "./eventFaq";

const cannesYachtingFestival: ChauffeurPage = {
  slug: "cannes-yachting-festival",
  title: "Chauffeur privé Cannes Yachting Festival – VTC UHNW",
  subtitle:
    "Chauffeur privé pendant le Cannes Yachting Festival : transferts aéroport ↔ Vieux Port et Port Canto, mises à disposition brokers et service discret UHNW.",
  keywords: [
    "chauffeur cannes yachting festival",
    "vtc cannes yachting festival",
    "chauffeur privé yachting festival cannes",
  ],
  hero: {
    title: "Chauffeur privé pendant le Cannes Yachting Festival",
    subtitle:
      "Mi-septembre, le plus grand salon nautique à flot d'Europe relie Vieux Port et Port Canto : un chauffeur discret pour propriétaires, brokers et visiteurs.",
    keywords: [
      "chauffeur Cannes Yachting Festival",
      "VTC Vieux Port Cannes",
      "chauffeur Port Canto",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Cannes Yachting Festival",
    badges: ["Aéroport ↔ Vieux Port", "Navette Port Canto", "Discrétion UHNW"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Yachting Festival",
    items: [
      {
        title: "Deux ports à relier",
        description:
          "Le salon s'étale sur le Vieux Port et le Port Canto, séparés par la Croisette : le chauffeur fait la navette entre les deux zones d'exposition.",
      },
      {
        title: "Aéroport ↔ ports en pic",
        description:
          "Nice Côte d'Azur concentre les arrivées mi-septembre : suivi de vol, accueil et liaison directe vers la jetée Albert Édouard ou Port Canto.",
      },
      {
        title: "Journée broker / client",
        description:
          "Visites de yachts, essais en mer et rendez-vous s'enchaînent : un chauffeur dédié à la demi-journée ou à la journée garde le rythme.",
      },
      {
        title: "Clientèle UHNW discrète",
        description:
          "Propriétaires, chantiers et family offices : chauffeur en tenue, confidentialité totale et ponctualité sur tout le salon.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Cannes Yachting Festival",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Cannes avec suivi de vol, dépose au plus près du Vieux Port ou du Port Canto selon vos rendez-vous.",
      },
      {
        title: "Navette Vieux Port ↔ Port Canto",
        text: "Allers-retours entre les deux sites d'exposition tout au long de la journée, sans chercher à se garer sur la Croisette.",
      },
      {
        title: "Mise à disposition brokers",
        text: "Chauffeur dédié à l'heure ou à la journée pour enchaîner visites de bateaux, essais et rendez-vous clients.",
      },
      {
        title: "Runs propriétaires & équipages",
        text: "Trajets discrets entre yacht, hôtel et chantiers pour propriétaires, invités et équipages pendant le salon.",
      },
      {
        title: "Dîners & soirées",
        text: "Réceptions à bord, dîners sur la Croisette et soirées professionnelles, avec retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Yachting Festival",
    items: [
      {
        name: "Vieux Port de Cannes",
        description:
          "Jetée Albert Édouard, espace Riviera et grands yachts à flot, cœur historique du salon.",
      },
      {
        name: "Port Canto",
        description:
          "Pôle Sailing / voiliers et yachts d'exception à l'autre extrémité de la Croisette.",
      },
      {
        name: "La Croisette & palaces",
        description:
          "Carlton, Martinez, Majestic : hébergement des exposants, brokers et visiteurs.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, arrivées concentrées mi-septembre.",
      },
      {
        name: "Cap d'Antibes & arrière-pays",
        description: "Villas, dîners privés et mouillages des unités invitées.",
      },
    ],
  },
  generic: {
    heading: "Le Cannes Yachting Festival vu d'un chauffeur",
    subheading:
      "Mi-septembre, Cannes devient la vitrine mondiale du yachting à flot — un public d'owners, brokers et UHNW sur deux ports.",
    paragraphs: [
      "Chaque mois de septembre, le Cannes Yachting Festival fait de la baie le plus grand salon nautique à flot d'Europe. L'exposition se déploie sur deux ports — le Vieux Port et son espace grands yachts, et le Port Canto dédié à la voile — reliés par la Croisette. Un chauffeur qui connaît Cannes fait la navette entre les deux sites sans vous imposer la recherche de stationnement.",
      "Les arrivées se concentrent à l'aéroport de Nice Côte d'Azur sur quelques jours. Suivi de vol, accueil et liaison directe vers la jetée Albert Édouard ou le Port Canto pour absorber le pic de la semaine et déposer au plus près de vos rendez-vous.",
      "Le salon enchaîne visites de yachts, essais en mer, rendez-vous chantiers et dîners à bord. La mise à disposition garde le même chauffeur pour propriétaires comme pour brokers, avec la discrétion attendue d'une clientèle UHNW et les retours de nuit après les réceptions.",
    ],
    bullets: [
      "Navette Vieux Port ↔ Port Canto",
      "Suivi de vol mi-septembre",
      "Discrétion UHNW",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Cannes Yachting Festival",
    steps: [
      "Indiquez vos dates, votre hôtel ou yacht et vos rendez-vous (Vieux Port, Port Canto, chantiers).",
      "Nous calons les liaisons aéroport ↔ ports et la navette entre les deux sites d'exposition.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition journée et soirée pour visites, essais en mer et dîners.",
    ],
  },
  faq: [
    ...cannesEventPriceFaq("fr", "pendant le Cannes Yachting Festival"),
    {
      q: "Comment réserver un chauffeur pendant le Cannes Yachting Festival ?",
      a: "Réservez en amont : mi-septembre, Cannes est très sollicitée par le salon. Transmettez vos dates, votre hôtel ou yacht et vos rendez-vous pour que nous bloquions un chauffeur dédié, avec la discrétion attendue d'une clientèle UHNW.",
    },
    {
      q: "Faites-vous la navette entre le Vieux Port et le Port Canto ?",
      a: "Oui. Le salon se tient sur deux ports séparés par la Croisette ; le chauffeur assure les allers-retours entre la jetée Albert Édouard et le Port Canto toute la journée, ce qui évite de chercher à stationner entre deux rendez-vous.",
    },
    {
      q: "Gérez-vous la liaison entre l'aéroport de Nice et les ports de Cannes ?",
      a: "Oui, avec suivi de vol et accueil. Nous assurons la liaison directe Nice ↔ Cannes et déposons au plus près du Vieux Port ou du Port Canto selon vos visites de bateaux et rendez-vous.",
    },
    {
      q: "Pouvez-vous transporter aussi les équipages et prestataires ?",
      a: "Oui. Au-delà des propriétaires et invités, nous organisons les runs de capitaines, équipages et prestataires entre yacht, hôtel et chantiers tout au long du salon.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur privé Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Chauffeur privé Cannes Saint-Tropez",
      href: "/chauffeur/transferts/cannes-saint-tropez",
    },
    {
      label: "Obtenir un devis",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Cannes Yachting Festival",
    text: "Aéroport ↔ ports, navette Vieux Port / Port Canto et service discret : réservez un chauffeur dédié pour le salon.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : page événement sans prix, même recette que le Festival.
    ...cannesEventSeo(
      "fr",
      "Cannes Yachting Festival",
      "Vieux-Port et Port Canto.",
    ),
    canonical: "/chauffeur/cannes-yachting-festival",
  },
};

export default cannesYachtingFestival;
